/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { ISelectableOption } from '@fluentui/react/lib/SelectableOption';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureTenantStore } from 'src/core/stores/AzureTenantStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getOrDefaultRegionFromResource } from 'src/core/utils/csAccountUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { azureResourceModalsMessages } from 'src/modules/azure/components/azureResourceModalsMessages';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';

export type AzureCsAccountSelectFormProps = {
    /**
     * The kind of the cs accounts to display.
     */
    mode: Extract<'LUIS' | 'LUIS.Authoring' | 'TextAnalytics' | 'TextTranslation', AzureCsAccountStore['kind']>;

    /**
     * Filter function used for filtering returned results.
     */
    filterPredicate: (account: AzureCsAccountStore) => boolean;

    /**
     * A callback for when the Azure Cognitive Service Resource
     * selection changes.
     */
    onCsAccountChange: (csAccount: AzureCsAccountStore) => void;

    /**
     * A callback for when the validation state changes.
     */
    onValidationChange?: (isValid: boolean) => void;
};

type AzureCsAccountSelectModalDto = {
    /**
     * The azure subscription to be used to create the new azure cs account.
     */
    subscriptionId: string;

    /**
     * The azure cs accounts for the user to select from.
     */
    csAccountPath: string;
};

const { styleSpan, styleComponent } = getStylistV2('AzureCsAccountSelectModal');
const AccountLabel = styleSpan(
    'AccountLabel',
    (theme, cssVars) => ({
        fontWeight: theme.accountInfoFontWeight as Property.FontWeight,
        marginRight: cssVars.marginRight
    }),
    {
        cssVars: { marginRight: '' },
        cssVarsSetter: ({ marginRight }: { marginRight: string }) => ({
            marginRight
        })
    }
);

const LabelContainer = styleComponent<ILabelProps>(Label)('LabelContainer', { display: 'flex' });

const dropdownStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

/**
 * Represents a modal to select azure cognitive
 * service account.
 */
export const AzureCsAccountSelectForm = observer((props: AzureCsAccountSelectFormProps) => {
    const { mode, filterPredicate, onCsAccountChange, onValidationChange } = props;

    const [csAccount, setCsAccount] = React.useState<AzureCsAccountStore>();

    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { tenantStoreHouse, subscriptionStoreHouse, session } = rootStore.azureStore;
    const { formState, updateFormState } = useFormState<AzureCsAccountSelectModalDto>({
        subscriptionId: [session.subscriptionId ?? '', FormValidators.createRequiredValidator()],
        csAccountPath: ['', FormValidators.createRequiredValidator()]
    });

    const currentRegion = getOrDefaultRegionFromResource(session.csAccountStore);

    React.useEffect(() => onValidationChange?.(formState.form.valid), [formState.form.valid]);

    React.useEffect(() => {
        const csAccountSelected = resources.find(a => a.path === formState.fields.csAccountPath.value);
        setCsAccount(csAccountSelected);
        onCsAccountChange(csAccountSelected);
    }, [formState.fields.csAccountPath.value]);

    const { resourcesStoreHouse } = useAzureResourcesManagement({ subscriptionId: formState.fields.subscriptionId.value });

    const resources = resourcesStoreHouse.items.filter(filterPredicate);
    const currentTenant: AzureTenantStore = tenantStoreHouse.findById(session.tenantId) || { id: '', name: '', defaultDomain: '' };

    const csAccountItems = resources.map<ISelectableOption>(item => ({
        key: item.path,
        text: item.name,
        data: { secondaryText: `(${item.region}, ${item.sku.code})` }
    }));

    let csAccountLabel = '';
    let csAccountPlaceholder = '';
    let csAccountCalloutContent: React.ReactNode = null;

    switch (mode) {
        case 'LUIS.Authoring':
            csAccountLabel = localize(azureResourceModalsMessages.csAuthoringAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.csAccountPlaceholderAuthoring);
            csAccountCalloutContent = (
                <>
                    {localize(azureResourceModalsMessages.csAuthoringAccountCallout, { region: currentRegion ?? '-' })}
                    &nbsp;
                    <ExternalLink
                        aria-label={localize(azureResourceModalsMessages.learnMoreRegions)}
                        href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-reference-regions">
                        {localize(globalMessages.learnMore)}
                    </ExternalLink>
                </>
            );
            break;
        case 'TextAnalytics':
            csAccountCalloutContent = '';
            csAccountLabel = localize(azureResourceModalsMessages.csLanguageAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.csAccountPlaceholderLanguage);
            break;
        case 'TextTranslation':
            csAccountCalloutContent = '';
            csAccountLabel = localize(azureResourceModalsMessages.csTranslatorAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.csAccountPlaceholderTranslator);
            break;
        default:
            csAccountLabel = localize(azureResourceModalsMessages.csPredictionAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.predictionCsAccountPlaceholder);
            csAccountCalloutContent = (
                <>
                    {localize(azureResourceModalsMessages.csPredictionAccountCallout)}
                    &nbsp;
                    <ExternalLink
                        aria-label={localize(azureResourceModalsMessages.learnMoreResources)}
                        href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-how-to-azure-subscription#luis-resources">
                        {localize(globalMessages.learnMore)}
                    </ExternalLink>
                </>
            );
    }

    // OnRender function for the accounts label
    const accountsLabelOnRender = () => (
        <Stack horizontal verticalAlign="center">
            <LabelContainer
                required
                id={csAccount?.id}
                aria-labelledby={csAccount?.id}
                styles={{ root: { '::after': { paddingRight: 0 } } }}>
                {csAccountLabel}
                &nbsp;
            </LabelContainer>
            {csAccountCalloutContent && <CalloutButton content={csAccountCalloutContent} />}
        </Stack>
    );

    const onSubscriptionChange = (item: IDropdownOption) => {
        let newFormState: Partial<AzureCsAccountSelectModalDto> = { subscriptionId: item?.key as string };

        if (formState.fields.csAccountPath.value && item?.key !== formState.fields.subscriptionId.value) {
            newFormState = { ...newFormState, csAccountPath: '' };
        }

        updateFormState(newFormState);
    };

    return (
        <Stack tokens={{ childrenGap: 20, padding: 1 }}>
            <Stack tokens={{ childrenGap: 2 }}>
                <LuisDropdown
                    required
                    maxHeight={300}
                    selectedKey={currentTenant.id}
                    isLoading={tenantStoreHouse.tracker.isRunning}
                    label={localize(azureResourceModalsMessages.tenantLabel)}
                    options={tenantStoreHouse.items.map(item => ({ key: item.id, text: item.name }))}
                    onChange={(_, opt) => dispatcher.dispatch('switchAzureTenantInSession', { id: opt.key as string })}
                />
                <LuisText variant="small">{localize(azureResourceModalsMessages.tenantDescription)}</LuisText>
            </Stack>

            <LuisDropdown
                required
                calloutProps={{ styles: dropdownStyles }}
                data-automation-id="subscriptionsDropdown"
                onChange={(_, opt) => onSubscriptionChange(opt)}
                selectedKey={formState.fields.subscriptionId.value}
                isLoading={subscriptionStoreHouse.tracker.isRunning}
                errorMessage={formState.fields.subscriptionId.errorMessage}
                label={localize(azureResourceModalsMessages.subscriptionLabel)}
                placeholder={localize(azureResourceModalsMessages.subscriptionPlaceholder)}
                options={subscriptionStoreHouse.items.map(item => ({ key: item.id, text: item.name }))}
            />

            <Stack tokens={{ childrenGap: 12 }}>
                <LuisDropdown
                    required
                    label={csAccountLabel}
                    options={csAccountItems}
                    ariaLabel={csAccountLabel}
                    selectedKey={csAccount?.path}
                    aria-labelledby={csAccount?.id}
                    placeholder={csAccountPlaceholder}
                    onRenderLabel={accountsLabelOnRender}
                    data-automation-id="csAccountsDropdown"
                    calloutProps={{ styles: dropdownStyles }}
                    isLoading={resourcesStoreHouse.tracker.isRunning}
                    disabled={!formState.fields.subscriptionId.valid}
                    errorMessage={formState.fields.csAccountPath.errorMessage}
                    onChange={(_, item) => updateFormState({ csAccountPath: item.key as string })}
                />

                {csAccount && (
                    <Stack tokens={{ childrenGap: 8 }}>
                        <LuisText variant="small">
                            <AccountLabel customProps={{ marginRight: '50px' }}>
                                {localize(azureResourceModalsMessages.pricingTier)}
                            </AccountLabel>
                            <LuisText fontWeight={400}>
                                {csAccount.sku.name} ({csAccount.sku.code})
                            </LuisText>
                        </LuisText>

                        <LuisText variant="small">
                            <AccountLabel customProps={{ marginRight: '12px' }}>
                                {localize(azureResourceModalsMessages.managedIdentity)}
                            </AccountLabel>
                            <LuisText fontWeight={400}>
                                {csAccount.isIdentityEnabled
                                    ? localize(azureResourceModalsMessages.enabled)
                                    : localize(azureResourceModalsMessages.disabled)}
                            </LuisText>
                        </LuisText>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
});
